import { ChangeDetectorRef, Component, HostListener, inject } from '@angular/core';
import { FormControl, FormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { CompanyService } from 'src/app/services/app/company.service';
import { Company } from 'src/app/models/common/CompanyLoginResp';
import { CookieService } from 'ngx-cookie-service';
import { LogService } from 'src/app/services/common/log.service';
import { AuthService } from 'src/app/services/app/auth.service';
import { SubscriberService } from 'src/app/services/app/subscriber.service';
import { SubscriberDetails } from 'src/app/models/settings/SubscriberDetails';
import { SubscriberLoginResp } from 'src/app/models/common/SubscriberLoginResp';
import { Platform } from '@ionic/angular';
import { firstValueFrom } from 'rxjs';
import { NgClass, NgStyle } from '@angular/common';
import { SelectModule } from 'primeng/select';
import { ButtonModule } from 'primeng/button';
import { MessageService } from 'primeng/api';
import { AppService } from 'src/app/services/app/app.service';
import { SignalrService } from 'src/app/services/app/signalr.service';
import { ModalNavigationService } from 'src/app/core/services/modal-navigation.service';

interface FinancialYear {
  value: string;
  viewValue: string;
}

@Component({
    standalone: true,
    selector: 'app-view-company',
    templateUrl: './view-company.component.html',
    styleUrls: ['./view-company.component.css'],
    imports: [NgClass, FormsModule, ReactiveFormsModule, SelectModule, ButtonModule, NgStyle],
})
export class ViewCompanyComponent {
  subscriberID: string;
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly modalNavigation = inject(ModalNavigationService);

  constructor(private router: Router,
    private authService: AuthService,
    private companyService: CompanyService,
    private route: ActivatedRoute,
    private cookieService:CookieService,
    private logService:LogService,
    private subscriberService:SubscriberService,
    private platform: Platform,
    private messageService: MessageService,
    private appService: AppService,
    private signalRService: SignalrService) { }

  /** Options for subscription modal business type (PrimeNG Select). */
  businessTypeOptions = [
    { label: 'General', value: 'General' },
    { label: 'Hotel', value: 'Hotel' },
  ];

  subscriberForm: FormGroup;
  deleteCompanyID: string = "";
  displayStyle = "none";
  allCompanies: Company[] = [];
  filteredCompanies: Company[] = [];
  displayUserName = 'Subscriber';

  financialyears: FinancialYear[] = [];
  formGroup: FormGroup;
  deleteForm: FormGroup;
  showFinancialYear = false;
  displaySubscriptionModal = "none";
  showModalClose = false;
  subscriptionDetailsFormGroup : FormGroup;
  isErrorMessage = 0;
  isPasswordMatchError = 0;

  hide = true;
  subscriberDetails: SubscriberDetails;

  subscriberResp: SubscriberLoginResp = null;

  searchGroup: FormGroup;

  isOTPSent: boolean = false;
  isSendingOTP = false;
  isOTPValid: boolean | null = null;
  /** Saved mobile from server; OTP only when form mobile differs from this. */
  originalMobileNumber = '';

  displayEnterModal = 'none';
  selectedCompany: Company | null = null;
  enterFinancialYear = '';
  enterFinancialYears: FinancialYear[] = [];
  isLoadingEnterFinancialYears = false;
  isEnteringCompany = false;

  companyMenuCompany: Company | null = null;
  companyMenuTop = 0;
  companyMenuLeft = 0;
  private companyMenuOpening = false;

  async ngOnInit() {
    try {
      this.authService.restorePersistedSubscriberSession();
      this.subscriberID =
        this.route.snapshot.queryParamMap.get('subscriberID') ??
        this.authService.getItemSync('subscriberID') ??
        '';
      this.displayUserName = this.resolvePortalDisplayName();

      this.route.queryParamMap.subscribe(params => {
        if (params.has('subscriberID')) {
          this.subscriberID = params.get('subscriberID') ?? this.subscriberID;
        }
      });

        this.GetAllCompanies();
        this.getSubscriberDetails();

      this.initSubscriptionDetailsForm();
    } catch (ex) {
      this.logService.InsertExceptionLog(ex, "View Company", "ngOnInit");
      console.log(ex);
    }

    this.InitForm();
  }

  private isUsableDisplayLabel(value: string | null | undefined): boolean {
    if (value == null) {
      return false;
    }
    const trimmed = value.trim();
    return trimmed !== '' && trimmed !== 'null' && trimmed !== 'undefined';
  }

  private resolvePortalDisplayName(): string {
    const username = this.authService.getItemSync('PortalUsername');
    if (this.isUsableDisplayLabel(username)) {
      return username!.trim();
    }
    return 'User';
  }

  private initSubscriptionDetailsForm() {
    this.subscriptionDetailsFormGroup = new FormGroup({
      SubscriberID: new FormControl(this.subscriberID),
      MobileNumber: new FormControl("", [Validators.required]),
      Address: new FormControl(),
      State: new FormControl(),
      City: new FormControl(),
      Pincode: new FormControl(),
      GstNo: new FormControl(),
      BusinessType: new FormControl('General', [Validators.required]),
      Password: new FormControl("", [Validators.required]),
      ConfirmPassword: new FormControl("", [Validators.required]),
      OTP: new FormControl('')
    });
  }

  private normalizeMobile(value: unknown): string {
    return String(value ?? '').replace(/\D/g, '').trim();
  }

  get isMobileNumberChanged(): boolean {
    const current = this.normalizeMobile(this.subscriptionDetailsFormGroup?.controls?.['MobileNumber']?.value);
    const original = this.normalizeMobile(this.originalMobileNumber);
    return current !== original;
  }

  syncOtpValidators(): void {
    const otpControl = this.subscriptionDetailsFormGroup?.controls?.['OTP'];
    if (!otpControl) {
      return;
    }

    if (this.isMobileNumberChanged) {
      otpControl.setValidators([Validators.required]);
    } else {
      otpControl.clearValidators();
      otpControl.setValue('');
      this.isOTPSent = false;
      this.isOTPValid = null;
    }
    otpControl.updateValueAndValidity({ emitEvent: false });
  }

  InitForm() {
    this.searchGroup = new FormGroup({
      search: new FormControl('')
    });

    this.deleteForm = new FormGroup({
      deleteCompanyFY: new FormControl(),
      financialYear: new FormControl()
    });
  }

  async logout(): Promise<void> {
    await this.authService.signOut();
  }

  applyFilter(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    this.updateFilteredCompanies(filterValue);
  }

  private updateFilteredCompanies(searchTerm = '') {
    const normalizedSearch = searchTerm.trim().toLowerCase();

    if (!normalizedSearch) {
      this.filteredCompanies = [...this.allCompanies];
      return;
    }

    this.filteredCompanies = this.allCompanies.filter((company) => {
      const companyCode = this.formatCompanyCode(company).toLowerCase();
      const companyId = String(company.companyId ?? '').toLowerCase();
      const companyName = String(company.name ?? '').toLowerCase();
      const srNo = String(company['srNo'] ?? '').toLowerCase();

      return (
        companyName.includes(normalizedSearch) ||
        companyId.includes(normalizedSearch) ||
        companyCode.includes(normalizedSearch) ||
        srNo.includes(normalizedSearch)
      );
    });
  }

  formatCompanyCode(company: Company): string {
    const rawId = String(company.companyId ?? '').replace(/\D/g, '');
    const paddedId = rawId.padStart(4, '0');
    return `Com_${paddedId}`;
  }

  getCompanyLocation(company: Company): string {
    return (
      company['address'] ||
      company['city'] ||
      company['location'] ||
      company['area'] ||
      '—'
    );
  }

  getLastSessionLabel(company: Company): string | null {
    const lastSession =
      company.lastSessionOn ||
      company['lastSession'] ||
      company['lastSessionDate'] ||
      company['lastLoginDate'];

    if (!lastSession) {
      return null;
    }

    return `Last Session: ${this.formatLastSession(lastSession)}`;
  }

  private formatLastSession(value: string | Date): string {
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) {
      return String(value);
    }

    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  }

  getStatValue(company: Company, field: 'totalUsers' | 'activeUsers'): string {
    const value = company[field];
    if (value === null || value === undefined || value === '') {
      return '00';
    }

    const numericValue = Number(value);
    if (Number.isNaN(numericValue)) {
      return String(value);
    }

    return numericValue.toString().padStart(2, '0');
  }

  GetAllCompanies() {
    try {
      this.allCompanies = [];

      this.authService.getPortalCompanies().subscribe(
        (result) => {
          this.allCompanies = (result as Company[]).map((company) => this.normalizePortalCompany(company));
          this.updateFilteredCompanies(this.searchGroup?.get('search')?.value ?? '');
        },
        () => {
          this.subscriberForm = new FormGroup({
            SubscriberID: new FormControl(this.subscriberID)
          });

          this.companyService.GetAllCompanies(this.subscriberForm.value).subscribe(
            (fallback) => {
              this.allCompanies = (fallback as Company[]).map((company) => ({
                ...this.normalizePortalCompany(company),
                accessStatus: company.accessStatus ?? 0,
                canEnter: company.canEnter ?? 0
              }));
              this.updateFilteredCompanies(this.searchGroup?.get('search')?.value ?? '');
            },
            (err: Error) => console.log(err.message)
          );
        }
      );
    }
    catch (ex) {
      this.logService.InsertExceptionLog(ex,"View Company","GetAllCompanies");
      console.log(ex);
    }
  }

  private normalizePortalCompany(company: Company): Company {
    const companyId = String(company.companyId ?? '');
    const fromApi =
      company.preferredFinancialYear ??
      company['PreferredFinancialYear'] ??
      null;
    const fromStore = this.readStoredPreferredFinancialYear(companyId);
    const preferred = String(fromApi ?? fromStore ?? '').trim();

    return {
      ...company,
      preferredFinancialYear: preferred || null,
    };
  }

  private preferredFyStorageKey(): string {
    const userId =
      this.authService.getItemSync('portalUserID') ??
      this.authService.getItemSync('UserID') ??
      this.subscriberID ??
      'anonymous';
    return `portalPreferredFy:${userId}`;
  }

  private readPreferredFyMap(): Record<string, string> {
    try {
      const raw = localStorage.getItem(this.preferredFyStorageKey());
      if (!raw) {
        return {};
      }
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
      return {};
    }
  }

  private readStoredPreferredFinancialYear(companyId: string): string {
    if (!companyId) {
      return '';
    }
    return String(this.readPreferredFyMap()[companyId] ?? '').trim();
  }

  private writeStoredPreferredFinancialYear(companyId: string | number, financialYear: string): void {
    const id = String(companyId ?? '').trim();
    const fy = String(financialYear ?? '').trim();
    if (!id || !fy) {
      return;
    }

    const map = this.readPreferredFyMap();
    map[id] = fy;
    localStorage.setItem(this.preferredFyStorageKey(), JSON.stringify(map));
  }

  private getPreferredFinancialYear(company: Company | null | undefined): string {
    if (!company) {
      return '';
    }

    const fromCompany = String(
      company.preferredFinancialYear ??
      company['PreferredFinancialYear'] ??
      ''
    ).trim();
    if (fromCompany) {
      return fromCompany;
    }

    return this.readStoredPreferredFinancialYear(String(company.companyId ?? ''));
  }

  private resolvePreferredFinancialYear(preferred: string, years: FinancialYear[]): string | null {
    const normalized = preferred.trim();
    if (!normalized || !years.length) {
      return null;
    }

    const exact = years.find(
      (year) => year.value === normalized || year.viewValue === normalized
    );
    if (exact) {
      return exact.value;
    }

    const preferredYear = normalized.includes('/')
      ? normalized.split('/').pop()
      : normalized;

    const byYear = years.find((year) => {
      const yearPart = year.value.includes('/')
        ? year.value.split('/').pop()
        : year.value;
      return yearPart === preferredYear || year.viewValue === preferredYear;
    });

    return byYear?.value ?? null;
  }

  onCompanyCardActivate(company: Company, event: Event): void {
    if (this.isEnteringCompany) {
      return;
    }

    const target = event.target as HTMLElement | null;
    if (target?.closest('.company-card-actions')) {
      return;
    }

    if (event instanceof KeyboardEvent && event.key === ' ') {
      event.preventDefault();
    }

    this.loginCompany(company);
  }

  loginCompany(company: Company) {
    const preferred = this.getPreferredFinancialYear(company);
    if (!preferred) {
      this.openEnterCompanyModal(company);
      return;
    }

    this.isEnteringCompany = true;
    this.selectedCompany = company;
    this.enterFinancialYear = preferred;
    this.formGroup = new FormGroup({
      CompanyID: new FormControl(Number(company.companyId))
    });

    this.authService.GetFinancialYear(this.formGroup.value).subscribe({
      next: (result) => {
        const years = this.mapFinancialYearsFromResponse(result);
        this.enterFinancialYear = this.resolvePreferredFinancialYear(preferred, years) ?? preferred;
        void this.confirmEnterCompany();
      },
      error: () => {
        this.enterFinancialYear = preferred;
        void this.confirmEnterCompany();
      }
    });
  }

  private mapFinancialYearsFromResponse(result: { financialYear?: string; FinancialYear?: string } | null | undefined): FinancialYear[] {
    const raw = result?.financialYear ?? result?.FinancialYear ?? '';
    return String(raw)
      .split(',')
      .map((year) => year.trim())
      .filter(Boolean)
      .map((value) => ({
        value,
        viewValue: value.includes('/') ? value.split('/')[2] : value,
      }));
  }

  openEnterCompanyModal(company: Company) {
    this.selectedCompany = company;
    this.enterFinancialYear = '';
    this.enterFinancialYears = [];
    this.isLoadingEnterFinancialYears = true;

    this.formGroup = new FormGroup({
      CompanyID: new FormControl(Number(company.companyId))
    });

    const preferred = this.getPreferredFinancialYear(company);

    this.authService.GetFinancialYear(this.formGroup.value).subscribe({
      next: (result) => {
        this.enterFinancialYears = this.mapFinancialYearsFromResponse(result);
        const resolved = this.resolvePreferredFinancialYear(preferred, this.enterFinancialYears);
        if (resolved) {
          this.enterFinancialYear = resolved;
        } else {
          this.enterFinancialYear = this.enterFinancialYears.length === 1
            ? this.enterFinancialYears[0].value
            : '';
        }
        this.isLoadingEnterFinancialYears = false;
        this.displayEnterModal = 'block';
      },
      error: (err: Error) => {
        this.isLoadingEnterFinancialYears = false;
        console.log(err.message);
        this.messageService.add({
          severity: 'error',
          summary: 'Financial Year',
          detail: 'Unable to load financial years for this company.',
          life: 5000,
        });
      }
    });
  }

  closeEnterCompanyModal() {
    this.displayEnterModal = 'none';
  }

  async confirmEnterCompany() {
    if (!this.selectedCompany?.companyId || !this.enterFinancialYear) {
      return;
    }

    try {
      this.isEnteringCompany = true;
      sessionStorage.setItem('CompanyID', String(this.selectedCompany.companyId));
      sessionStorage.setItem('CompanyName', this.selectedCompany.name ?? '');
      sessionStorage.removeItem('ctoken');
      sessionStorage.removeItem('companyUser');
      sessionStorage.removeItem('RoleID');
      sessionStorage.removeItem('daysLeft');

      const result = await firstValueFrom(this.authService.selectCompany({
        CompanyID: this.selectedCompany.companyId,
        FinancialYear: this.enterFinancialYear
      }));

      if (result !== 1) {
        this.messageService.add({
          severity: 'error',
          summary: 'Access denied',
          detail: 'You do not have access to this company.',
          life: 5000,
        });
        this.isEnteringCompany = false;
        return;
      }

      this.selectedCompany.preferredFinancialYear = this.enterFinancialYear;
      const companyInList = this.allCompanies.find(
        (item) => String(item.companyId) === String(this.selectedCompany?.companyId)
      );
      if (companyInList) {
        companyInList.preferredFinancialYear = this.enterFinancialYear;
      }
      this.writeStoredPreferredFinancialYear(this.selectedCompany.companyId, this.enterFinancialYear);

      sessionStorage.setItem('FinancialYear', this.enterFinancialYear);
      const daysLeft = sessionStorage.getItem('daysLeft');
      if (daysLeft === '-1') {
        this.messageService.add({
          severity: 'warn',
          summary: 'Subscription ended',
          detail: 'Please renew your package.',
          life: 5000,
        });
        this.isEnteringCompany = false;
        return;
      }

      this.appService.loadInitialData(+this.selectedCompany.companyId, this.enterFinancialYear).subscribe({
        next: (success) => {
          this.isEnteringCompany = false;
          if (success) {
            this.signalRService.startConnection(String(this.selectedCompany.companyId));
            this.closeEnterCompanyModal();
            this.cdr.detectChanges();
            this.modalNavigation.navigateWhenModalLayersClear(() => this.router.navigate(['admin']));
          }
        },
        error: () => {
          this.isEnteringCompany = false;
        }
      });
    } catch (ex) {
      this.isEnteringCompany = false;
      this.logService.InsertExceptionLog(ex, 'View Company', 'confirmEnterCompany');
    }
  }

  GetFinancialYear() {
    try {
      this.financialyears = [];
      this.formGroup = new FormGroup({
        CompanyID: new FormControl(this.deleteCompanyID)
      });

      if (this.formGroup.valid) {
        this.authService.GetFinancialYear(this.formGroup.value).subscribe(
          (result) => {
            this.financialyears = this.mapFinancialYearsFromResponse(result);
          }, (err: Error) => {
            console.log(err.message);
          })
      }
    }
    catch (ex) {
      this.logService.InsertExceptionLog(ex,"View Company","GetAllCompanies");
      console.log(ex);
    }
  }

  getSubscriberDetails() {
    try {
      this.subscriberForm = new FormGroup({
        SubscriberID: new FormControl(this.subscriberID)
      });

      this.subscriberService.GetSubscriberDataForSubscriberID(this.subscriberForm.value).subscribe(
        (result: SubscriberDetails) => {
          this.subscriberDetails = result;
          this.displayUserName = this.resolvePortalDisplayName();
          const clean = (value: unknown): string =>
            this.isUsableDisplayLabel(value == null ? null : String(value)) ? String(value).trim() : '';

          this.originalMobileNumber = this.normalizeMobile(clean(this.subscriberDetails.mobileNumber));
          this.subscriptionDetailsFormGroup.patchValue({
            MobileNumber: clean(this.subscriberDetails.mobileNumber),
            Address: clean(this.subscriberDetails.address),
            State: clean(this.subscriberDetails.state),
            City: clean(this.subscriberDetails.city),
            Pincode: this.subscriberDetails.pincode || '',
            GstNo: clean(this.subscriberDetails.gstNo),
            BusinessType: clean(this.subscriberDetails.businessType),
            Password: clean(this.subscriberDetails.password),
            ConfirmPassword: clean(this.subscriberDetails.password),
            OTP: ''
          });
          this.isOTPSent = false;
          this.isOTPValid = null;
          this.syncOtpValidators();

          var mobileNumber = +this.subscriberDetails.mobileNumber;
          if (mobileNumber == null || mobileNumber == 0) {
            this.showModalClose = false;
            this.displaySubscriptionModal = "block";
          } else {
            this.showModalClose = true;
          }
        }, (err: Error) => {
          console.log(err.message);
        });
    }
    catch (ex) {
      this.logService.InsertExceptionLog(ex,"View Company","getSubscriberDetails");
      console.log(ex);
    }
  }

  onChangeDeleteOption() {
    if (this.deleteForm.controls['deleteCompanyFY'].value === 'financialYear') {
      this.showFinancialYear = true;
    } else {
      this.showFinancialYear = false;
    }
  }

  openSubscriptionDetailsModal() {
    this.displaySubscriptionModal = "block";
  }

  closeSubscriptionDetailsModal() {
    this.displaySubscriptionModal = "none";
  }

  redirectToAddCompany() {
    this.router.navigate(
      ['add-company']);
  }

  redirectToPayment(){
    this.router.navigate(['payment'],{ queryParams: { subscriberID: this.subscriberID } });
  }

  closepopup() {
    this.displayStyle = "none";
  }

  toggleCompanyMenu(company: Company, event: MouseEvent) {
    event.stopPropagation();
    event.preventDefault();

    if (this.companyMenuCompany?.companyId === company.companyId) {
      this.closeCompanyMenu();
      return;
    }

    this.positionCompanyCardMenu(event.currentTarget as HTMLElement);
    this.companyMenuCompany = company;
    this.companyMenuOpening = true;
    queueMicrotask(() => {
      this.companyMenuOpening = false;
    });
  }

  isCompanyMenuOpen(company: Company): boolean {
    return this.companyMenuCompany?.companyId === company.companyId;
  }

  closeCompanyMenu() {
    this.companyMenuCompany = null;
  }

  private positionCompanyCardMenu(trigger: HTMLElement): void {
    const menuWidth = 171;
    const menuHeight = 80;
    const gap = 4;
    const rect = trigger.getBoundingClientRect();
    let left = rect.right - menuWidth;
    let top = rect.bottom + gap;
    const viewportWidth = typeof window !== 'undefined' ? window.innerWidth : 0;
    const viewportHeight = typeof window !== 'undefined' ? window.innerHeight : 0;

    if (viewportWidth && left + menuWidth > viewportWidth - 8) {
      left = Math.max(8, viewportWidth - menuWidth - 8);
    }
    if (left < 8) {
      left = 8;
    }
    if (viewportHeight && top + menuHeight > viewportHeight - 8) {
      top = rect.top - menuHeight - gap;
    }
    if (top < 8) {
      top = 8;
    }

    this.companyMenuTop = top;
    this.companyMenuLeft = left;
  }

  onCompanyMenuChangeFy(company: Company) {
    this.closeCompanyMenu();
    this.openEnterCompanyModal(company);
  }

  openDeleteCompanyModal(company: Company) {
    this.closeCompanyMenu();
    this.deleteCompanyID = company.companyId;
    this.deleteForm.patchValue({
      deleteCompanyFY: 'company',
      financialYear: null,
    });
    this.showFinancialYear = false;
    this.GetFinancialYear();
    this.displayStyle = 'block';
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    if (this.companyMenuOpening || !this.companyMenuCompany) {
      return;
    }

    const target = event.target as HTMLElement | null;
    if (target?.closest('.company-card-icon-btn--menu') || target?.closest('.figma-table-col-menu-panel')) {
      return;
    }

    this.closeCompanyMenu();
  }

  deleteCompany() {
    if (this.deleteForm.controls['deleteCompanyFY'].value === 'company') {
      try {
        this.router.navigate(
          ['login'],
          { queryParams: { deleteMode: 'company', companyID: this.deleteCompanyID } }
        );
      } catch (ex) {
        console.log(ex);
      }
    } else if (this.deleteForm.controls['deleteCompanyFY'].value === 'financialYear') {
      if (this.deleteForm.controls['financialYear'].value !== null) {
        let financialYear = this.deleteForm.controls['financialYear'].value;
        try {
          this.router.navigate(
            ['login'],
            { queryParams: { deleteMode: 'financialYear', companyID: this.deleteCompanyID, financialYear: financialYear } }
          );
        } catch (ex) {
          console.log(ex);
        }
      }
    }
  }

  get canSendOtp(): boolean {
    return this.normalizeMobile(this.subscriptionDetailsFormGroup?.controls?.['MobileNumber']?.value).length === 10;
  }

  onMobileNumberInput() {
    this.syncOtpValidators();
    if (!this.isMobileNumberChanged) {
      this.isOTPSent = false;
      this.isOTPValid = null;
    }
  }

  sendOTP() {
    if (!this.isMobileNumberChanged || this.isSendingOTP) {
      this.isOTPSent = false;
      return;
    }

    const mobileNumber = this.normalizeMobile(this.subscriptionDetailsFormGroup.controls['MobileNumber'].value);
    if (mobileNumber.length !== 10) {
      this.isOTPSent = false;
      return;
    }

    try {
      this.isSendingOTP = true;
      this.subscriberService.SendOTP({ contact: mobileNumber }).subscribe(
        () => {
          this.isOTPSent = true;
          this.isOTPValid = null;
          this.isSendingOTP = false;
        },
        (err: Error) => {
          console.log(err.message);
          this.isOTPSent = false;
          this.isSendingOTP = false;
        });
    } catch (ex) {
      console.log(ex);
      this.isOTPSent = false;
      this.isSendingOTP = false;
    }
  }

  async verifyOTP(): Promise<boolean> {
    const mobileNumber = this.normalizeMobile(this.subscriptionDetailsFormGroup.controls['MobileNumber'].value);
    const otp = String(this.subscriptionDetailsFormGroup.controls['OTP'].value ?? '').trim();

    if (mobileNumber && otp) {
      try {
        const result = await firstValueFrom(
          this.subscriberService.VerifyOTP({ contact: mobileNumber, otp })
        );
        return result?.message === 'OTP verified successfully';
      } catch (error) {
        console.log(error);
        return false;
      }
    }

    return false;
  }

  async UpdateSubscriberDetails(){
    try{
      this.isErrorMessage = 0;
      this.isPasswordMatchError = 0;
      this.syncOtpValidators();

      if(this.subscriptionDetailsFormGroup.value.MobileNumber == "" || this.subscriptionDetailsFormGroup.value.Password == "" || this.subscriptionDetailsFormGroup.value.ConfirmPassword == ""){
        this.isErrorMessage = 1;
      } else if(this.subscriptionDetailsFormGroup.value.Password !== this.subscriptionDetailsFormGroup.value.ConfirmPassword) {
        this.isPasswordMatchError = 1;
      }
      else{
        if (!this.subscriptionDetailsFormGroup.valid) {
          return;
        }

        if (this.isMobileNumberChanged) {
          if (!this.isOTPSent) {
            this.isOTPValid = false;
            return;
          }

          this.isOTPValid = await this.verifyOTP();
          if (!this.isOTPValid) {
            return;
          }
        } else {
          this.isOTPValid = null;
        }

        this.subscriberService.UpdateSubscriberDetails(this.subscriptionDetailsFormGroup.value).subscribe(
          (result) => {
            if (result.isSuccess == 1) {
              this.originalMobileNumber = this.normalizeMobile(this.subscriptionDetailsFormGroup.value.MobileNumber);
              this.isOTPSent = false;
              this.isOTPValid = null;
              this.syncOtpValidators();
              sessionStorage.setItem("isSubscritionDetails", "true");
              sessionStorage.setItem("businessType", this.subscriptionDetailsFormGroup.value.BusinessType);
              this.displaySubscriptionModal = "none";
              this.messageService.add({
                severity: 'success',
                summary: 'Saved',
                detail: 'Subscription details added successfully.',
                life: 5000,
              });
            }
          }, (err: Error) => {
            console.log(err.message);
          });
      }      
    }
    catch(ex){
      console.log("Error in UpdateSubscriberDetails : " + ex);
    }
  }
}
